// -----------------------------------------------------------------------------
// People Plus AI page constants
// This file contains only reusable constants and tiny pure helper functions.
// Keeping these outside the page file makes the main component cleaner.
// -----------------------------------------------------------------------------

// Fields that can be searched in the PAIC page
export const SEARCHABLE_FIELDS = [
  "role_type",
  "legal_entity",
  "business_unit",
  "location",
  "function",
  "sub_function",
  "job_level",
];

// Keys used for table filters
export const FILTER_KEYS = [
  "role_type",
  "legal_entity",
  "business_unit",
  "location",
  "function",
  "sub_function",
  "job_level",
];

// Initial empty filter state for all filterable columns
export const INITIAL_FILTERS = Object.fromEntries(
  FILTER_KEYS.map((key) => [key, []]),
);

// Initial dropdown open/close state for all filter dropdowns
export const INITIAL_DROPDOWN_STATE = Object.fromEntries(
  FILTER_KEYS.map((key) => [key, false]),
);

// Initial search text state for each filter dropdown
export const INITIAL_SEARCH_INPUTS = Object.fromEntries(
  FILTER_KEYS.map((key) => [key, ""]),
);

// Table header configuration for the PAIC table
export const TABLE_HEADERS = [
  { key: "legal_entity", label: "Legal Entity" },
  { key: "business_unit", label: "Business Unit" },
  { key: "role_type", label: "Role Type" },
  { key: "location", label: "Location" },
  { key: "function", label: "Function" },
  { key: "sub_function", label: "Sub Function" },
  { key: "job_level", label: "Job Level" },
];

// Agent Library — tag colours for the Status column
export const AGENT_STATUS_TAG_COLORS = {
  Completed: "green",
  Processing: "gold",
  Failed: "red",
};

// Agent Library — summary tiles shown above the agent table
// View Modes

export const PAIC_VIEW_MODES = {
  READ: "READ",
  EDIT: "EDIT",
  VIEW: "VIEW",
};

// Document Types

export const PAIC_DOCUMENT_TYPES = {
  AGENT_JD: "agent-jd",
  AGENT_PRD: "agent-prd",
  PEOPLE_JD: "people-jd",
};

// Tab Keys

export const PAIC_TAB_KEYS = {
  JD: "jd",
  JOBS_LIBRARY: "jobs-library",
  AGENTS_LIBRARY: "agents-library",
  PEOPLE_AI: "people-plus-ai",
  SKILLS_BRIDGER: "skills-bridger",
  LEARNING_CATALYST: "learning-catalyst",
  TALENT_MOBILITY: "talent-mobility",
};

// Dropdown Menu Items

export const PAIC_MENU_ITEMS = {
  FOR_AGENT: "for-agent",
  FOR_ROLE: "for-role",
};

// Document Type Options

export const AGENT_JD_DOCUMENT_OPTIONS = [
  { value: PAIC_DOCUMENT_TYPES.AGENT_JD, label: "Agent JD" },
  { value: PAIC_DOCUMENT_TYPES.AGENT_PRD, label: "Agent PRD" },
];

export const PEOPLE_JD_DOCUMENT_OPTIONS = [
  { value: PAIC_DOCUMENT_TYPES.PEOPLE_JD, label: "People JD" },
];

// View Mode Display Text

export const VIEW_MODE_LABELS = {
  [PAIC_VIEW_MODES.EDIT]: "Edit",
  [PAIC_VIEW_MODES.READ]: "Read Only",
  [PAIC_VIEW_MODES.VIEW]: "View",
};

// Selection Status

export const PAIC_SELECTION_STATUS = {
  PROCESSING: "Processing",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

export const PAIC_SELECTION_STATUS_COLOR = {
  [PAIC_SELECTION_STATUS.PROCESSING]: "processing",
  [PAIC_SELECTION_STATUS.COMPLETED]: "success",
  [PAIC_SELECTION_STATUS.FAILED]: "error",
};

// Project Status

export const PAIC_PROJECT_STATUS = {
  CREATED: "Created",
  PROCESSING: "Processing",
  COMPLETED: "Completed",
  FAILED: "Failed",
};

// Archive Status

export const PAIC_ARCHIVE_STATUS = {
  ARCHIVED: "ARCHIVED",
  ACTIVE: "ACTIVE",
};

// Project Source (used to distinguish owned vs delegated in merged lists)

export const PAIC_PROJECT_SOURCE = {
  OWNED: "owned",
  DELEGATED: "delegated",
  SHARED: "shared",
};

// Agent Library — map the agent's access label to the PAIC project source
export const AGENT_SOURCE_TO_PROJECT_SOURCE = {
  Owner: PAIC_PROJECT_SOURCE.OWNED,
  Delegated: PAIC_PROJECT_SOURCE.DELEGATED,
  Shared: PAIC_PROJECT_SOURCE.SHARED,
};

export const MANAGE_GROUPS_TABS = {
  MY_GROUPS: "my-groups",
  DELEGATED_TO_ME: "delegated-to-me",
  SHARED_WITH_ME: "shared-with-me",
};

// Error Codes (numeric — matches backend error_code values)

export const PAIC_ERROR_CODES = {
  MISSING_REQUEST_BODY: 7000,
  VALIDATION_ERROR: 7002,
  PROJECT_NOT_FOUND: 7026,
  INTERNAL_SERVER_ERROR: 7110,
  UNEXPECTED_ERROR: 7111,
  NO_PUBLISHED_JD: 7112,
  DUPLICATE_PROJECT_NAME: 7202,
  DUPLICATE_JOB_IDS: 7204,
  PROJECT_RUN_EXISTS: 7206,
  DUPLICATE_AGENT_NAME: 7208,
  PAIC_RUN_NOT_FAILED: 7212,
  NOT_PROJECT_OWNER: 7214,
  USER_NOT_FOUND: 7206,
  ACCESS_VALIDATION_ERROR: 7220,
  DELEGATE_VALIDATION_FAILED: 7224,
  AUTHORIZATION_ERROR: 7401,
};

// Maps error codes to short notification titles (API provides the detailed message)
const PAIC_ERROR_TITLES = {
  [PAIC_ERROR_CODES.DUPLICATE_PROJECT_NAME]: 'Duplicate Group Name',
  [PAIC_ERROR_CODES.DUPLICATE_JOB_IDS]: 'Duplicate Job Selection',
  [PAIC_ERROR_CODES.PROJECT_RUN_EXISTS]: 'Run Already Exists',
  [PAIC_ERROR_CODES.DUPLICATE_AGENT_NAME]: 'Duplicate Agent Name',
  [PAIC_ERROR_CODES.PAIC_RUN_NOT_FAILED]: 'Cannot Re-initiate',
  [PAIC_ERROR_CODES.INTERNAL_SERVER_ERROR]: 'Server Error',
  [PAIC_ERROR_CODES.UNEXPECTED_ERROR]: 'Unexpected Error',
  [PAIC_ERROR_CODES.NO_PUBLISHED_JD]: 'No Published JD',
  [PAIC_ERROR_CODES.NOT_PROJECT_OWNER]: 'Ownership Permission Error',
  [PAIC_ERROR_CODES.ACCESS_VALIDATION_ERROR]: 'Access Validation Error',
  [PAIC_ERROR_CODES.DELEGATE_VALIDATION_FAILED]: 'Delegate Validation Failed',
};

/**
 * Centralized PAIC API error handler.
 *
 * Strategy:
 * - 401: Session expired → prompt re-login
 * - 400: Show API message directly (backend sends user-friendly messages);
 *        picks a smart title from PAIC_ERROR_TITLES based on error_code
 * - 500+: Generic server error
 * - Unknown: Returns false → caller handles with its own fallback
 *
 * @param {object} error - Axios error object
 * @param {function} notifyError - SbNotification.error or wrapper function
 * @returns {boolean} true if handled, false otherwise
 */
export const handlePAICErrorCode = (error, notifyError) => {
  const status = error?.response?.status;
  const errCode = error?.response?.data?.error_code;
  const apiMessage = error?.response?.data?.message;
  const failures = error?.response?.data?.failed || error?.response?.data?.failures || [];

  // 401 — Session expired
  if (status === 401) {
    notifyError('Session Expired', 'Please log in again.');
    return true;
  }

  // 400 — Use API message directly, pick smart title from error code
  if (status === 400 && apiMessage) {
    const title = PAIC_ERROR_TITLES[errCode] || 'Request Failed';
    const failureDetails = Array.isArray(failures) && failures.length > 0
      ? failures.map((f) => `${f.email || f.user || 'User'}: ${f.reason || f.message || 'Validation failed'}`).join(', ')
      : '';
    notifyError(title, failureDetails ? `${apiMessage} ${failureDetails}` : apiMessage);
    return true;
  }

  // 500+ — Server error
  if (status >= 500) {
    notifyError('Server Error', apiMessage || 'Something went wrong. Please try again.');
    return true;
  }

  return false;
};

// Fallback Error Messages (used when handlePAICErrorCode returns false)

export const PAIC_VIEW_ONLY_MESSAGE = {
  title: "View-Only Access",
  description:
    "This project has been shared with you for viewing only. You cannot modify agent selections, edit documents, or submit versions.",
};

export const PAIC_ERROR_MESSAGES = {
  LOAD_ROLES_FAILED: 'Failed to load roles',
  FETCH_JOBS_FAILED: 'Unable to fetch inferred jobs. Please try again.',
  LOAD_GROUPS_FAILED: 'Failed to load groups',
  FETCH_GROUPS_FAILED: 'Unable to fetch PAIC groups. Please try again.',
  DUPLICATE_PROJECT: 'Duplicate Project Name',
  CREATE_GROUP_FAILED: 'Unable to create group',
  INVALID_GROUP: 'Invalid group selection',
  EVALUATION_FAILED: 'Evaluation failed',
  LOAD_AGENTS_FAILED: 'Unable to load agents',
  SUBMIT_AGENTS_FAILED: 'Unable to submit selected agents',
  SOMETHING_WENT_WRONG: 'Something went wrong while creating the group.',
  UNABLE_TO_START_EVALUATION: 'Unable to start PAIC evaluation for this group. Please try again.',
  LOAD_ROLE_IMPACT_FAILED: 'Unable to load role impact data',
  LOAD_AGENT_DOCUMENTS_FAILED: 'Unable to load agent documents',
  LOAD_PEOPLE_JD_FAILED: 'Unable to load job descriptions',
};

// Success Messages

export const PAIC_SUCCESS_MESSAGES = {
  GROUP_CREATED: "Group created successfully",
  AGENTS_SUBMITTED: "Selected agents submitted successfully",
  EVALUATION_INITIATED: "PAIC evaluation initiated",
  EVALUATION_STARTED: "PAIC evaluation started",
};

// Warning Messages

export const PAIC_WARNING_MESSAGES = {
  DUPLICATE_JOB_SELECTION: "Duplicate Job Selection",
  GROUP_EXISTS_WITH_JDS: "A group already exists with the selected JDs",
};

// Page Titles

export const PAIC_PAGE_TITLES = {
  MAIN: "People+AI Collaboration (PAIC)",
  JD_PRD_VIEW: "Job Details: People + AI Agent PRD",
  PEOPLE_JD_VIEW: "Job Details: People + AI Job Descriptions",
  GROUP_PREFIX: "Group : ",
};

// UI Labels

export const PAIC_LABELS = {
  VIEW_BY: "View By:",
  AGENTS: "Agents",
  ROLES: "Roles",
  SUBMIT: "Submit",
  SELECT_VERSION: "Select Version",
  NEW_ORG_DESIGN: "New Org Design",
  CREATE_NEW_GROUP: "Create New Group:",
  GIVE_NAME_PLACEHOLDER: "Give it a name and click enter",
  SELECT_OR_CREATE_GROUP: "Select or create group",
  SEARCH_ROLE_PLACEHOLDER: "Search role by keyword, skill, etc.",
};

// Button Labels

export const PAIC_BUTTON_LABELS = {
  EVALUATE_PAIC: "Evaluate PAIC Potential",
  VIEW_PAIC: "View PAIC Potential",
  RE_EVALUATE_PAIC: "Re-Evaluate PAIC Potential",
  PROCESSING_PAIC: "Processing PAIC Potential",
  PROCEED: "Proceed",
  CANCEL: "Cancel",
};

// Modal Content

export const PAIC_MODAL_CONTENT = {
  START_EVALUATION_TITLE: "Start PAIC evaluation?",
  START_EVALUATION_CONTENT: "This will start PAIC evaluation for the selected group.",
  RE_RUN_EVALUATION_TITLE: "Re-run PAIC evaluation?",
  RE_RUN_EVALUATION_CONTENT: "This will re-run PAIC evaluation for the selected group.",
};

// Sidebar Stats Card Constants

export const PAIC_STATS_PRIMARY_ITEMS = [
  { key: "roles", label: "Roles" },
  { key: "positions", label: "Positions" },
  { key: "agents", label: "AI Agents" },
];

export const PAIC_STATS_SECONDARY_ITEMS = [
  { key: "aiPotential", label: "AI Potential" },
  { key: "estimatedCost", label: "Est. Cost" },
];

// Small helper to normalize values before comparing them
// This helps avoid issues caused by null, undefined, casing, or extra spaces
export const normalize = (val) => String(val ?? "--").trim().toLowerCase();